import { Form } from 'antd'
import type { FormItemProps as AntdFormItemProps } from 'antd'
import React, { useEffect, useState } from 'react'

import { FormControl } from '@/components/lemmala-design-system/form-control/form-control'

interface FormItemProps extends Omit<AntdFormItemProps, 'label'> {
  label?: React.ReactNode
  optional?: boolean
  helperText?: React.ReactNode
  htmlFor?: string
  children: React.ReactNode
}

export const FormItem: React.FC<FormItemProps> = ({
  label,
  optional,
  helperText,
  htmlFor,
  children,
  ...antdFormItemProps
}) => {
  const form = Form.useFormInstance()
  const [fieldState, setFieldState] = useState({ hasError: false, errorText: '' })

  const fieldName = antdFormItemProps.name

  // Watch form field state changes
  useEffect(() => {
    if (!fieldName) return undefined

    const updateFieldState = () => {
      const errors = form.getFieldError(fieldName)
      const touched = form.isFieldTouched(fieldName)
      const hasError = touched && errors.length > 0

      setFieldState({
        hasError,
        errorText: hasError ? errors[0] : '',
      })
    }

    // Initial update
    updateFieldState()

    // Set up interval to check for changes
    const interval = setInterval(updateFieldState, 100)
    return function cleanup() {
      clearInterval(interval)
    }
  }, [form, fieldName])

  return (
    <div className='mb-4'>
      <FormControl
        label={label}
        optional={optional}
        helperText={helperText}
        errorText={fieldState.errorText || undefined}
        state={fieldState.hasError ? 'error' : 'default'}
        htmlFor={htmlFor}
      >
        <Form.Item
          {...antdFormItemProps}
          help={false} // Disable Antd's help text, we use FormControl
          style={{ marginBottom: 0 }} // Remove Antd's margin
        >
          {/* Clone the child element and add our styling props */}
          {React.isValidElement(children)
            ? React.cloneElement(
                children as React.ReactElement<{ state?: 'error' | 'default'; id?: string }>,
                {
                  state: fieldState.hasError ? 'error' : 'default',
                  id: htmlFor,
                },
              )
            : children}
        </Form.Item>
      </FormControl>
    </div>
  )
}
